import React, { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

type ModalSize = "sm" | "md" | "lg" | "xl" | "xxxl" | "xxxxl";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;

  title?: React.ReactNode;
  description?: React.ReactNode;

  children?: React.ReactNode;
  footer?: React.ReactNode;

  size?: ModalSize;
  widthClass?: string;

  showCloseButton?: boolean;
  backdropClassName?: string;
  className?: string;

  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;

  /** Eğer verirsen açıldığında ilk buna focus atar */
  initialFocusRef?: React.RefObject<HTMLElement>;

  /** İçerik alanı için ek class */
  contentClassName?: string;

  /** Header’ı gizlemek istersen */
  hideHeader?: boolean;

  /** Panel ref’i dışarıdan gerekirse */
  panelRef?: React.RefObject<HTMLDivElement>;
}

const sizeToPanelClass: Record<ModalSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-3xl",
  // büyük: geniş ama yine de güvenli sınırlarla
  xxxl: "sm:max-w-screen-2xl sm:h-auto sm:max-h-[85vh]",
  xxxxl: "sm:max-w-none sm:h-auto sm:max-h-[92vh] sm:w-[92vw] sm:min-w-[92vw]",
};

function ensureModalRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("modal-root") as HTMLElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "modal-root";
    document.body.appendChild(el);
  }
  return el;
}

function getFocusable(container: HTMLElement) {
  const selectors = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  const nodes = Array.from(container.querySelectorAll<HTMLElement>(selectors));
  // görünmeyenleri ele
  return nodes.filter((n) => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length));
}

function lockBodyScroll() {
  const body = document.body;
  const prevOverflow = body.style.overflow;
  const prevPaddingRight = body.style.paddingRight;

  // scrollbar genişliği (layout shift önlemek için)
  const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = "hidden";
  if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;

  return () => {
    body.style.overflow = prevOverflow;
    body.style.paddingRight = prevPaddingRight;
  };
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,

  title,
  description,
  children,
  footer,

  size = "lg",
  widthClass,

  showCloseButton = true,
  backdropClassName = "bg-black/50 backdrop-blur-sm",
  className = "",

  closeOnBackdrop = true,
  closeOnEsc = true,

  initialFocusRef,
  contentClassName = "",
  hideHeader = false,
  panelRef: externalPanelRef,
}) => {
  const reactId = useId();
  const titleId = useMemo(() => `modal-title-${reactId}`, [reactId]);
  const descId = useMemo(() => `modal-desc-${reactId}`, [reactId]);

  const internalPanelRef = useRef<HTMLDivElement>(null);
  const panelRef = externalPanelRef ?? internalPanelRef;

  const lastActiveElRef = useRef<HTMLElement | null>(null);

  const portalRoot = typeof window !== "undefined" ? ensureModalRoot() : null;

  // ESC + Focus trap
  useEffect(() => {
    if (!isOpen) return;

    lastActiveElRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusables = getFocusable(panel);
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!e.shiftKey) {
        // Tab
        if (active === last || !panel.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      } else {
        // Shift+Tab
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, closeOnEsc, panelRef]);

  // Body scroll lock (layout shift fixli)
  useEffect(() => {
    if (!isOpen) return;
    const unlock = lockBodyScroll();
    return unlock;
  }, [isOpen]);

  // İlk focus + restore focus
  useEffect(() => {
    if (!isOpen) return;

    const t = window.setTimeout(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
        return;
      }
      const panel = panelRef.current;
      if (!panel) return;

      const focusables = getFocusable(panel);
      (focusables[0] ?? panel).focus();
    }, 0);

    return () => window.clearTimeout(t);
  }, [isOpen, initialFocusRef, panelRef]);

  useEffect(() => {
    if (isOpen) return;
    // kapanınca focus’u geri ver
    const el = lastActiveElRef.current;
    if (el && typeof el.focus === "function") {
      // küçük bir tick: AnimatePresence exit sonrası daha stabil
      const t = window.setTimeout(() => el.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);

  if (!portalRoot) return null;

  const onBackdropMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closeOnBackdrop) return;
    // sadece backdrop’a tıklanınca kapat
    if (e.target === e.currentTarget) onClose();
  };

  const panelWidth = widthClass ? widthClass : sizeToPanelClass[size];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={[
            "fixed inset-0 z-[1000] flex",
            "items-end sm:items-center",
            "p-0 sm:p-4",
            backdropClassName,
          ].join(" ")}
          onMouseDown={onBackdropMouseDown}
        >
          <motion.div
            key="modal-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descId : undefined}
            tabIndex={-1}
            initial={{ opacity: 0, y: 18, scale: 1 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.995 }}
            transition={{ type: "spring", stiffness: 280, damping: 24, mass: 0.6 }}
            // mousedown panel içinde olursa backdrop kapatma tetiklenmesin
            onMouseDown={(e) => e.stopPropagation()}
            className={[
              "relative w-full",
              // mobil: sheet gibi tam yükseklik
              "h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[85vh]",
              panelWidth,
              "rounded-none sm:rounded-xl",
              "bg-white shadow-2xl outline-none",
              "flex flex-col overflow-hidden",
              "pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
              className,
            ].join(" ")}
          >
            {!hideHeader && (title || showCloseButton || description) && (
              <div className="sticky top-0 z-10 border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75">
                <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3">
                  <div className="min-w-0">
                    {title && (
                      <h3
                        id={titleId}
                        className="truncate text-base sm:text-lg font-semibold text-gray-900"
                      >
                        {title}
                      </h3>
                    )}
                    {description && (
                      <div id={descId} className="mt-1 text-sm text-gray-600">
                        {description}
                      </div>
                    )}
                  </div>

                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Kapat"
                      className="shrink-0 rounded-md p-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <X className="h-5 w-5 text-gray-700" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div
              className={[
                "flex-1 min-h-0 overflow-y-auto overscroll-contain",
                "px-4 sm:px-5 py-4",
                contentClassName,
              ].join(" ")}
            >
              {children}
            </div>

            {footer && (
              <div className="sticky bottom-0 z-10 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
                <div className="flex items-center justify-end gap-2 px-4 sm:px-5 py-3">
                  {footer}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    portalRoot
  );
};

export default Modal;
